import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
 clearCourseCalendarCache,
 shouldClearCourseCalendarCache,
} from "../utils/courseCalendarCache";
import { isAccessTokenExpired } from "../utils/authToken";

const configuredBaseURL =
 window._env_?.REACT_APP_API_URL?.trim() ||
 process.env.REACT_APP_API_URL?.trim();

if (!configuredBaseURL) {
 throw new Error("REACT_APP_API_URL is required");
}

const baseURL = configuredBaseURL.replace(/\/+$/, "");

if (!/^https?:\/\//i.test(baseURL)) {
 throw new Error("REACT_APP_API_URL must start with http:// or https://");
}

if (/\/api\/api$/i.test(baseURL)) {
 throw new Error("REACT_APP_API_URL must not contain duplicate /api/api");
}

const notificationBaseURL = baseURL.replace(/\/api$/i, "");
const notificationHubURL = `${notificationBaseURL}/notificationHub`;
const REFRESH_TIMEOUT_MS = 10_000;

const axiosInstance = axios.create({ baseURL });

const isPanelRoute = () =>
 typeof window !== "undefined" &&
 window.location.pathname.startsWith("/panel");

const clearAuthTokens = () => {
 localStorage.removeItem("authToken");
 localStorage.removeItem("refreshToken");
 delete axiosInstance.defaults.headers.common.Authorization;
};

const redirectToLoginIfNeeded = () => {
 if (isPanelRoute()) {
  window.location.href = "/giris";
 }
};

const setAuthHeader = (config, token) => {
 if (!config.headers) {
  config.headers = {};
 }
 if (typeof config.headers.set === "function") {
  config.headers.set("Authorization", `Bearer ${token}`);
 } else {
  config.headers.Authorization = `Bearer ${token}`;
 }
};

let isRefreshing = false;
let refreshWaitQueue = [];
let refreshPromise = null;

function resolveRefreshQueue(error, token = null) {
 refreshWaitQueue.forEach(({ resolve, reject }) => {
  if (error) reject(error);
  else resolve(token);
 });
 refreshWaitQueue = [];
}

async function refreshAccessToken() {
 if (refreshPromise) {
  return refreshPromise;
 }

 const refreshToken = localStorage.getItem("refreshToken");
 if (!refreshToken) {
  throw new Error("NO_REFRESH_TOKEN");
 }

 isRefreshing = true;
 refreshPromise = axios
  .post(`${baseURL}/auth/CreateAccessTokenWithRefreshToken`, refreshToken, {
   headers: {
    Accept: "*/*",
    "Content-Type": "application/json-patch+json",
   },
   timeout: REFRESH_TIMEOUT_MS,
  })
  .then(({ data }) => {
   const accessToken = data?.accessToken?.token;
   const nextRefresh = data?.refreshToken?.token;
   if (!accessToken) {
    throw new Error("INVALID_REFRESH_RESPONSE");
   }

   localStorage.setItem("authToken", accessToken);
   if (nextRefresh) {
    localStorage.setItem("refreshToken", `"${nextRefresh}"`);
   }

   axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
   resolveRefreshQueue(null, accessToken);
   return accessToken;
  })
  .catch((refreshError) => {
   resolveRefreshQueue(refreshError, null);
   clearAuthTokens();
   throw refreshError;
  })
  .finally(() => {
   isRefreshing = false;
   refreshPromise = null;
  });

 return refreshPromise;
}

async function ensureFreshAccessToken() {
 const refreshToken = localStorage.getItem("refreshToken");
 if (!refreshToken) {
  throw new Error("NO_REFRESH_TOKEN");
 }
 return refreshAccessToken();
}

async function retryWithoutAuth(originalRequest) {
 if (originalRequest.headers) {
  if (typeof originalRequest.headers.delete === "function") {
   originalRequest.headers.delete("Authorization");
  } else {
   delete originalRequest.headers.Authorization;
   delete originalRequest.headers.authorization;
  }
 }
 originalRequest._skipAuth = true;
 return axiosInstance(originalRequest);
}

axiosInstance.interceptors.request.use(async (config) => {
 if (config._skipAuth) {
  return config;
 }

 let token = localStorage.getItem("authToken");
 const refreshToken = localStorage.getItem("refreshToken");

 if (refreshToken && (!token || isAccessTokenExpired(token))) {
  try {
   token = await refreshAccessToken();
  } catch {
  }
 }

 if (token) {
  setAuthHeader(config, token);
 }
 return config;
});

axiosInstance.interceptors.response.use(
 (response) => {
  if (shouldClearCourseCalendarCache(response.config)) {
   clearCourseCalendarCache();
  }
  return response;
 },
 async (error) => {
  const originalRequest = error.config;
  const status = error?.response?.status;

  if (status === 401 && originalRequest && !originalRequest._retry) {
   originalRequest._retry = true;

   const refreshToken = localStorage.getItem("refreshToken");
   if (!refreshToken) {
    clearAuthTokens();
    if (isPanelRoute()) {
     redirectToLoginIfNeeded();
     return Promise.reject(error);
    }
    return retryWithoutAuth(originalRequest);
   }

   if (isRefreshing) {
    try {
     const token = await new Promise((resolve, reject) => {
      refreshWaitQueue.push({ resolve, reject });
     });
     setAuthHeader(originalRequest, token);
     return axiosInstance(originalRequest);
    } catch (queueError) {
     if (!isPanelRoute()) {
      return retryWithoutAuth(originalRequest);
     }
     redirectToLoginIfNeeded();
     return Promise.reject(queueError);
    }
   }

   try {
    const token = await refreshAccessToken();
    setAuthHeader(originalRequest, token);
    return axiosInstance(originalRequest);
   } catch (refreshError) {
    if (!isPanelRoute()) {
     return retryWithoutAuth(originalRequest);
    }
    redirectToLoginIfNeeded();
    return Promise.reject(refreshError);
   }
  }

  if (status === 401) {
   if (isPanelRoute()) {
    redirectToLoginIfNeeded();
   }
   return Promise.reject(error);
  }

  if (error.code === "ECONNABORTED" || error.code === "ERR_CANCELED") {
   return Promise.reject(error);
  }

  if (!error.response) {
   return Promise.reject(error);
  }

  if (error.config?.skipErrorToast) {
   return Promise.reject(error);
  }

  try {
   const message = error?.response?.data?.Detail;
   if (message == "") {
    let validErrorMessage = error?.response?.data?.Errors[0]["ErrorMessage"];
    toast.error(validErrorMessage, {
     autoClose: 1500,
     position: "top-center",
    });
   } else {
    if (message == "You are not authorized.") {
     toast.error("Beklenmedik bir hata oluştu ... ", {
      position: "top-center",
      autoClose: 2000,
     });
    } else {
     toast.error(message ?? "Bir şeyler yanlış gitti. tekrar deneyiniz.", {
      autoClose: 1500,
      position: "top-center",
     });
    }
   }
  } catch (err) {
   toast.error("Bir şeyler yanlış gitti. Lütfen tekrar deneyin.", {
    autoClose: 1500,
   });
  }
  return Promise.reject(error);
 }
);

const fetcher = (url) => axiosInstance.get(url).then((res) => res.data);

export {
 axiosInstance,
 fetcher,
 baseURL,
 notificationHubURL,
 ensureFreshAccessToken,
 clearAuthTokens,
};
